import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Grid,
    MenuItem,
    Typography,
    Box,
    CircularProgress,
    Divider,
    IconButton,
    Chip,
    FormControl,
    InputLabel,
    Select
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DescriptionIcon from '@mui/icons-material/Description';
import { GuestService } from '../../../api/services/guestService';
import { MediaService } from '../../../api/services/mediaService';
import { toast } from '../../../utils/toast';
import { SUPPORTED_PHONE_CODES, getMinPhoneLength } from '../../../utils/phoneValidation';

const initialFormState = {
    title: '',
    name: '',
    email: '',
    phoneCode: '+91',
    phone: '',
    gender: '',
    address: '',
    zip: '',
    country: '',
    state: '',
    city: '',
    idType: '',
    idProof: '',
    issuingCountry: '',
    issuingCity: '',
    idExpiryDate: '',
    idDocumentUrl: '',
    birthDate: '',
    birthCountry: '',
    nationality: ''
};

const TITLES = ['Mr.', 'Mrs.', 'Ms.', 'Dr.', 'Prof.'];
const GENDERS = ['Male', 'Female', 'Other'];
const ID_TYPES = [
    'Aadhaar',
    'Passport',
    'PAN Card',
    'Voter ID',
    'Driving Licence',
    'National ID'
];

export default function CreateGuestModal({ open, onClose, tenantId, onSuccess, editData }) {
    const [formData, setFormData] = useState(initialFormState);
    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isUploadingId, setIsUploadingId] = useState(false);
    const [previewOpen, setPreviewOpen] = useState(false);

    const handleIdFileUpload = async (file) => {
        if (!file) return;

        const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'application/pdf'];
        if (!allowedTypes.includes(file.type)) {
            toast.error('Please upload an image (JPG, PNG, WEBP) or PDF file.');
            return;
        }

        if (file.size > 10 * 1024 * 1024) {
            toast.error('File size exceeds the 10MB limit.');
            return;
        }

        setIsUploadingId(true);
        try {
            const res = await MediaService.uploadFile(file);
            const url = res?.url || res?.publicUrl || (typeof res === 'string' ? res : '');
            if (!url) throw new Error('No URL returned from media upload service');

            setFormData(prev => ({
                ...prev,
                idDocumentUrl: url
            }));
            toast.success('ID document uploaded successfully!');
        } catch (err) {
            console.error('Failed to upload ID document:', err);
            toast.error(err?.response?.data?.message || err?.message || 'Failed to upload ID document. Please try again.');
        } finally {
            setIsUploadingId(false);
        }
    };

    const handleRemoveIdDocument = () => {
        setFormData(prev => ({
            ...prev,
            idDocumentUrl: ''
        }));
    };

    useEffect(() => {
        if (!open) {
            setFormData(initialFormState);
            setErrors({});
            setPreviewOpen(false);
        } else if (editData) {
            // Format dates to YYYY-MM-DD for date inputs
            const formatDate = (dateStr) => dateStr ? dateStr.split('T')[0] : '';
            
            let normalizedIdType = editData.idType || '';
            if (normalizedIdType.toLowerCase() === 'adhar' || normalizedIdType.toLowerCase() === 'aadhar') {
                normalizedIdType = 'Aadhaar';
            } else if (normalizedIdType.toLowerCase() === 'pancard' || normalizedIdType.toLowerCase() === 'pan card' || normalizedIdType.toLowerCase() === 'pan') {
                normalizedIdType = 'PAN Card';
            }

            const rawPhone = editData.phone || editData.phoneNumber || '';
            const phoneMatch = rawPhone.match(/^(\+\d{1,4})\s*(.+)$/);
            const initialCode = editData.phoneCode || (phoneMatch ? phoneMatch[1] : '+91');
            const initialPhone = phoneMatch ? phoneMatch[2].replace(/\D/g, '') : (rawPhone.startsWith('+') ? rawPhone.replace(/^\+\d{1,4}\s*/, '').replace(/\D/g, '') : rawPhone);

            setFormData({
                title: editData.title || '',
                name: editData.name || '',
                email: editData.email || '',
                phoneCode: initialCode,
                phone: initialPhone,
                gender: editData.gender || '',
                address: editData.address || '',
                zip: editData.zip || '',
                country: editData.country || '',
                state: editData.state || '',
                city: editData.city || '',
                idType: normalizedIdType,
                idProof: editData.idProof || '',
                issuingCountry: editData.issuingCountry || '',
                issuingCity: editData.issuingCity || '',
                idExpiryDate: formatDate(editData.idExpiryDate),
                idDocumentUrl: editData.idDocumentUrl || '',
                birthDate: formatDate(editData.birthDate),
                birthCountry: editData.birthCountry || '',
                nationality: editData.nationality || ''
            });
            setErrors({});
        }
    }, [open, editData]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        
        // Input restriction logic
        if (name === 'name' && value && !/^[a-zA-Z\s'-]+$/.test(value)) return;
        if (name === 'phone' && value) {
            const match = value.match(/^(\+\d{1,4})\s*(.+)$/);
            if (match && SUPPORTED_PHONE_CODES.includes(match[1])) {
                setFormData(prev => ({
                    ...prev,
                    phoneCode: match[1],
                    phone: match[2].replace(/\D/g, '').slice(0, 15)
                }));
                if (errors.phone) setErrors(prev => ({ ...prev, phone: null }));
                return;
            }
            if (!/^[\d\s+()-]+$/.test(value)) return;
        }
        
        const letterOnlyFields = ['birthCountry', 'nationality', 'city', 'state', 'country', 'issuingCountry', 'issuingCity'];
        if (letterOnlyFields.includes(name) && value && !/^[a-zA-Z\s'-]+$/.test(value)) return;

        if (name === 'zip' && value && !/^[a-zA-Z0-9\s-]+$/.test(value)) return;
        if (name === 'idProof' && value && !/^[a-zA-Z0-9\s/-]+$/.test(value)) return;

        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        
        // Clear error when user types
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: null }));
        }
    };

    const validateForm = () => {
        const newErrors = {};
        
        if (!formData.name?.trim()) {
            newErrors.name = 'Full name is required';
        } else if (!/^[a-zA-Z\s'-]+$/.test(formData.name)) {
            newErrors.name = 'Name can only contain letters, spaces, hyphens, and apostrophes';
        }
        
        if (!formData.phone?.trim()) {
            newErrors.phone = 'Phone number is required';
        } else if (!/^[\d\s+()-]+$/.test(formData.phone)) {
            newErrors.phone = 'Invalid phone number format';
        } else {
            const minDigits = getMinPhoneLength(formData.phoneCode || '+91');
            const cleanDigits = formData.phone.replace(/[\s+()-]/g, '');
            if (cleanDigits.length < minDigits || cleanDigits.length > 15) {
                newErrors.phone = `Phone number must contain at least ${minDigits} digits`;
            }
        }

        if (!formData.email?.trim()) {
            newErrors.email = 'Email address is required';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
            newErrors.email = 'Invalid email format (e.g. guest@example.com)';
        }

        const letterOnlyFields = [
            { key: 'birthCountry', label: 'Birth Country' },
            { key: 'nationality', label: 'Nationality' },
            { key: 'city', label: 'City' },
            { key: 'state', label: 'State/Province' },
            { key: 'country', label: 'Country' },
            { key: 'issuingCountry', label: 'Issuing Country' },
            { key: 'issuingCity', label: 'Issuing City' }
        ];

        letterOnlyFields.forEach(field => {
            if (formData[field.key] && !/^[a-zA-Z\s'-]+$/.test(formData[field.key])) {
                newErrors[field.key] = `${field.label} can only contain letters`;
            }
        });

        if (formData.zip && !/^[a-zA-Z0-9\s-]+$/.test(formData.zip)) {
            newErrors.zip = 'Invalid ZIP/Postal code format';
        }
        
        if (formData.idProof && !/^[a-zA-Z0-9\s/-]+$/.test(formData.idProof)) {
            newErrors.idProof = 'Invalid ID/Passport number format';
        }

        setErrors(newErrors);
        const errorKeys = Object.keys(newErrors);
        return {
            isValid: errorKeys.length === 0,
            firstError: errorKeys.length > 0 ? newErrors[errorKeys[0]] : null
        };
    };

    const handleSubmit = async () => {
        const { isValid, firstError } = validateForm();
        if (!isValid) {
            toast.error(firstError || 'Please fill in all required fields correctly.');
            return;
        }

        setIsSubmitting(true);
        try {
            const payload = {};
            Object.keys(formData).forEach(key => {
                const val = formData[key];
                if (val !== null && val !== undefined && val !== '') {
                    payload[key] = typeof val === 'string' ? val.trim() : val;
                }
            });

            if (formData.phone) {
                const digits = formData.phone.replace(/\D/g, '');
                const code = formData.phoneCode || '+91';
                payload.phoneCode = code;
                payload.phoneNumber = digits;
                payload.phone = `${code} ${digits}`;
            }

            if (editData && editData.id) {
                await GuestService.updateGuest(editData.id, payload, tenantId);
                toast.success('Guest updated successfully!');
            } else {
                await GuestService.createGuest(payload, tenantId);
                toast.success('Guest created successfully!');
            }
            
            if (onSuccess) onSuccess();
            setFormData(initialFormState);
            setErrors({});
            onClose();
        } catch (error) {
            console.error('Failed to save guest:', error);
            const errorMsg = error.response?.data?.message || error.message || 'Failed to save guest';
            toast.error(errorMsg);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <>
        <Dialog open={open} onClose={!isSubmitting ? onClose : undefined} maxWidth="md" fullWidth>
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <PersonAddIcon sx={{ mr: 1, color: 'primary.main' }} />
                    <Typography variant="h6" fontWeight="bold">
                        {editData ? 'Edit Guest Profile' : 'Create New Guest'}
                    </Typography>
                </Box>
                <IconButton onClick={onClose} size="small">
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <Divider />
            
            <DialogContent sx={{ p: 4 }}>
                <Typography variant="subtitle2" color="primary" sx={{ mb: 2, fontWeight: 'bold', textTransform: 'uppercase' }}>
                    Personal Information
                </Typography>
                <Grid container spacing={2} sx={{ mb: 4 }}>
                    <Grid item xs={12} sm={2}>
                        <TextField
                            select
                            fullWidth
                            label="Title"
                            name="title"
                            value={formData.title}
                            onChange={handleChange}
                            size="small"
                        >
                            {TITLES.map(title => (
                                <MenuItem key={title} value={title}>{title}</MenuItem>
                            ))}
                        </TextField>
                    </Grid>
                    <Grid item xs={12} sm={5}>
                        <TextField
                            fullWidth
                            label="Full Name *"
                            name="name"
                            value={formData.name}
                            onChange={handleChange}
                            error={!!errors.name}
                            helperText={errors.name}
                            size="small"
                        />
                    </Grid>
                    <Grid item xs={12} sm={5}>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            <FormControl size="small" sx={{ minWidth: 85 }} disabled={isSubmitting}>
                                <InputLabel>Code</InputLabel>
                                <Select
                                    value={formData.phoneCode || '+91'}
                                    label="Code"
                                    name="phoneCode"
                                    onChange={handleChange}
                                >
                                    {SUPPORTED_PHONE_CODES.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                                </Select>
                            </FormControl>
                            <TextField
                                fullWidth
                                label="Phone *"
                                name="phone"
                                value={formData.phone}
                                onChange={handleChange}
                                error={!!errors.phone}
                                helperText={errors.phone}
                                size="small"
                                disabled={isSubmitting}
                            />
                        </Box>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            label="Email Address *"
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                            error={!!errors.email}
                            helperText={errors.email}
                            size="small"
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            select
                            fullWidth
                            label="Gender"
                            name="gender"
                            value={formData.gender}
                            onChange={handleChange}
                            size="small"
                        >
                            {GENDERS.map(g => (
                                <MenuItem key={g} value={g}>{g}</MenuItem>
                            ))}
                        </TextField>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <TextField
                            fullWidth
                            label="Date of Birth"
                            name="birthDate"
                            type="date"
                            value={formData.birthDate}
                            onChange={handleChange}
                            InputLabelProps={{ shrink: true }}
                            size="small"
                        />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <TextField
                            fullWidth
                            label="Birth Country"
                            name="birthCountry"
                            value={formData.birthCountry}
                            onChange={handleChange}
                            error={!!errors.birthCountry}
                            helperText={errors.birthCountry}
                            size="small"
                        />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <TextField
                            fullWidth
                            label="Nationality"
                            name="nationality"
                            value={formData.nationality}
                            onChange={handleChange}
                            error={!!errors.nationality}
                            helperText={errors.nationality}
                            size="small"
                        />
                    </Grid>
                </Grid>

                <Typography variant="subtitle2" color="primary" sx={{ mb: 2, fontWeight: 'bold', textTransform: 'uppercase' }}>
                    Address Information
                </Typography>
                <Grid container spacing={2} sx={{ mb: 4 }}>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="Street Address"
                            name="address"
                            value={formData.address}
                            onChange={handleChange}
                            size="small"
                        />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                        <TextField
                            fullWidth
                            label="City"
                            name="city"
                            value={formData.city}
                            onChange={handleChange}
                            error={!!errors.city}
                            helperText={errors.city}
                            size="small"
                        />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                        <TextField
                            fullWidth
                            label="State/Province"
                            name="state"
                            value={formData.state}
                            onChange={handleChange}
                            error={!!errors.state}
                            helperText={errors.state}
                            size="small"
                        />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                        <TextField
                            fullWidth
                            label="Country"
                            name="country"
                            value={formData.country}
                            onChange={handleChange}
                            error={!!errors.country}
                            helperText={errors.country}
                            size="small"
                        />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                        <TextField
                            fullWidth
                            label="ZIP/Postal Code"
                            name="zip"
                            value={formData.zip}
                            onChange={handleChange}
                            error={!!errors.zip}
                            helperText={errors.zip}
                            size="small"
                        />
                    </Grid>
                </Grid>

                <Typography variant="subtitle2" color="primary" sx={{ mb: 2, fontWeight: 'bold', textTransform: 'uppercase' }}>
                    Identity Documents
                </Typography>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            select
                            fullWidth
                            label="ID Document Type"
                            name="idType"
                            value={formData.idType}
                            onChange={handleChange}
                            size="small"
                        >
                            {ID_TYPES.map(type => (
                                <MenuItem key={type} value={type}>{type}</MenuItem>
                            ))}
                            {formData.idType && !ID_TYPES.includes(formData.idType) && (
                                <MenuItem key={formData.idType} value={formData.idType}>{formData.idType}</MenuItem>
                            )}
                        </TextField>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            label="ID/Passport Number"
                            name="idProof"
                            value={formData.idProof}
                            onChange={handleChange}
                            error={!!errors.idProof}
                            helperText={errors.idProof}
                            size="small"
                        />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <TextField
                            fullWidth
                            label="Issuing Country"
                            name="issuingCountry"
                            value={formData.issuingCountry}
                            onChange={handleChange}
                            error={!!errors.issuingCountry}
                            helperText={errors.issuingCountry}
                            size="small"
                        />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <TextField
                            fullWidth
                            label="Issuing City"
                            name="issuingCity"
                            value={formData.issuingCity}
                            onChange={handleChange}
                            error={!!errors.issuingCity}
                            helperText={errors.issuingCity}
                            size="small"
                        />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <TextField
                            fullWidth
                            label="Expiry Date"
                            name="idExpiryDate"
                            type="date"
                            value={formData.idExpiryDate}
                            onChange={handleChange}
                            InputLabelProps={{ shrink: true }}
                            size="small"
                        />
                    </Grid>

                    {/* ID Document Upload Section */}
                    <Grid item xs={12}>
                        <Typography variant="body2" fontWeight={600} sx={{ mb: 1, color: '#334155' }}>
                            Upload ID Document (Proof)
                        </Typography>
                        
                        {isUploadingId ? (
                            <Box sx={{
                                p: 3.5,
                                border: '2px dashed #cbd5e1',
                                borderRadius: 2.5,
                                bgcolor: '#f8fafc',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: 1.5
                            }}>
                                <CircularProgress size={30} />
                                <Typography variant="body2" color="text.secondary" fontWeight={500}>
                                    Uploading ID document to cloud storage...
                                </Typography>
                            </Box>
                        ) : formData.idDocumentUrl ? (
                            <Box sx={{
                                p: 2,
                                border: '1px solid #e2e8f0',
                                borderRadius: 2.5,
                                bgcolor: '#ffffff',
                                boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                flexWrap: 'wrap',
                                gap: 2
                            }}>
                                <Box 
                                    sx={{ 
                                        display: 'flex', 
                                        alignItems: 'center', 
                                        gap: 2, 
                                        minWidth: 0, 
                                        cursor: 'pointer',
                                        '&:hover .preview-img': { transform: 'scale(1.05)' }
                                    }}
                                    onClick={() => setPreviewOpen(true)}
                                    title="Click to preview"
                                >
                                    {formData.idDocumentUrl.toLowerCase().endsWith('.pdf') || formData.idDocumentUrl.toLowerCase().includes('/pdf') ? (
                                        <Box sx={{ 
                                            width: 48, 
                                            height: 48, 
                                            borderRadius: 2, 
                                            bgcolor: '#fee2e2', 
                                            color: '#dc2626', 
                                            display: 'flex', 
                                            alignItems: 'center', 
                                            justifyContent: 'center', 
                                            flexShrink: 0 
                                        }}>
                                            <DescriptionIcon sx={{ fontSize: 28 }} />
                                        </Box>
                                    ) : (
                                        <Box 
                                            component="img" 
                                            className="preview-img"
                                            src={formData.idDocumentUrl} 
                                            alt="ID Document" 
                                            sx={{ 
                                                width: 52, 
                                                height: 52, 
                                                borderRadius: 2, 
                                                objectFit: 'cover', 
                                                border: '1px solid #e2e8f0',
                                                boxShadow: '0 2px 6px rgba(0,0,0,0.06)',
                                                flexShrink: 0,
                                                transition: 'transform 0.2s ease'
                                            }} 
                                        />
                                    )}
                                    <Box sx={{ minWidth: 0 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Typography variant="body2" fontWeight={700} sx={{ color: '#0f172a' }}>
                                                ID Document Attached
                                            </Typography>
                                            <Chip 
                                                label="Uploaded" 
                                                size="small" 
                                                color="success" 
                                                sx={{ height: 20, fontSize: '0.675rem', fontWeight: 700 }} 
                                            />
                                        </Box>
                                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                                            Click View to preview • Ready to save
                                        </Typography>
                                    </Box>
                                </Box>

                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        startIcon={<VisibilityIcon />}
                                        onClick={() => setPreviewOpen(true)}
                                        sx={{ 
                                            textTransform: 'none', 
                                            borderRadius: 2, 
                                            fontWeight: 600,
                                            borderColor: '#e2e8f0',
                                            color: 'primary.main',
                                            '&:hover': { borderColor: 'primary.main', bgcolor: 'primary.50' }
                                        }}
                                    >
                                        View
                                    </Button>
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        component="label"
                                        sx={{ 
                                            textTransform: 'none', 
                                            borderRadius: 2, 
                                            fontWeight: 600,
                                            borderColor: '#e2e8f0',
                                            color: 'text.secondary',
                                            '&:hover': { bgcolor: '#f8fafc', borderColor: '#cbd5e1' }
                                        }}
                                    >
                                        Replace
                                        <input
                                            type="file"
                                            hidden
                                            accept="image/jpeg,image/png,image/webp,application/pdf"
                                            onChange={(e) => {
                                                if (e.target.files?.[0]) handleIdFileUpload(e.target.files[0]);
                                            }}
                                        />
                                    </Button>
                                    <IconButton
                                        size="small"
                                        color="error"
                                        onClick={handleRemoveIdDocument}
                                        title="Remove Document"
                                        sx={{ 
                                            bgcolor: 'rgba(239, 68, 68, 0.08)', 
                                            '&:hover': { bgcolor: 'rgba(239, 68, 68, 0.16)' },
                                            borderRadius: 2,
                                            p: 0.75
                                        }}
                                    >
                                        <DeleteOutlineIcon fontSize="small" />
                                    </IconButton>
                                </Box>
                            </Box>
                        ) : (
                            <Box
                                onDragOver={(e) => e.preventDefault()}
                                onDrop={(e) => {
                                    e.preventDefault();
                                    if (e.dataTransfer.files?.[0]) {
                                        handleIdFileUpload(e.dataTransfer.files[0]);
                                    }
                                }}
                                sx={{
                                    border: '2px dashed #cbd5e1',
                                    borderRadius: 2.5,
                                    p: 3,
                                    textAlign: 'center',
                                    bgcolor: '#f8fafc',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s',
                                    '&:hover': {
                                        borderColor: 'primary.main',
                                        bgcolor: '#eff6ff'
                                    }
                                }}
                                onClick={() => {
                                    const input = document.createElement('input');
                                    input.type = 'file';
                                    input.accept = 'image/jpeg,image/png,image/webp,application/pdf';
                                    input.onchange = (e) => {
                                        if (e.target.files?.[0]) {
                                            handleIdFileUpload(e.target.files[0]);
                                        }
                                    };
                                    input.click();
                                }}
                            >
                                <CloudUploadIcon sx={{ fontSize: 36, color: '#6366f1', mb: 0.5 }} />
                                <Typography variant="body2" fontWeight={600} color="#1e293b">
                                    Click or drag to upload ID document
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    Supports JPG, PNG, WEBP, PDF (Max 10MB)
                                </Typography>
                            </Box>
                        )}
                    </Grid>
                </Grid>

            </DialogContent>
            <Divider />
            <DialogActions sx={{ p: 3, pt: 0, justifyContent: 'flex-end', gap: 1 }}>
                <Button onClick={onClose} variant="text" sx={{ fontWeight: 'bold' }}>
                    Cancel
                </Button>
                <Button 
                    onClick={handleSubmit} 
                    variant="contained" 
                    color="primary"
                    disabled={isSubmitting}
                    startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : <PersonAddIcon />}
                    sx={{ fontWeight: 'bold', minWidth: 140 }}
                >
                    {isSubmitting ? (editData ? 'Updating...' : 'Creating...') : (editData ? 'Save Changes' : 'Create Guest')}
                </Button>
            </DialogActions>
        </Dialog>

        {/* In-app ID Document Preview Dialog */}
        <Dialog 
            open={previewOpen} 
            onClose={() => setPreviewOpen(false)} 
            maxWidth="md" 
            fullWidth
            sx={{ zIndex: 1400 }}
        >
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <VisibilityIcon color="primary" />
                    <Typography variant="h6" fontWeight="bold">
                        ID Document Preview
                    </Typography>
                </Box>
                <IconButton onClick={() => setPreviewOpen(false)} size="small">
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <Divider />
            <DialogContent sx={{ p: 2.5, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 350, bgcolor: '#f8fafc' }}>
                {formData.idDocumentUrl && (formData.idDocumentUrl.toLowerCase().endsWith('.pdf') || formData.idDocumentUrl.toLowerCase().includes('/pdf')) ? (
                    <Box 
                        component="iframe" 
                        src={formData.idDocumentUrl} 
                        title="PDF ID Document" 
                        sx={{ width: '100%', height: '70vh', border: 'none', borderRadius: 2 }} 
                    />
                ) : (
                    <Box 
                        component="img" 
                        src={formData.idDocumentUrl} 
                        alt="ID Document Preview" 
                        sx={{ 
                            maxWidth: '100%', 
                            maxHeight: '75vh', 
                            objectFit: 'contain', 
                            borderRadius: 2,
                            boxShadow: '0 4px 20px rgba(0,0,0,0.1)' 
                        }} 
                    />
                )}
            </DialogContent>
            <DialogActions sx={{ p: 2, px: 3 }}>
                <Button onClick={() => setPreviewOpen(false)} variant="contained" color="primary" sx={{ fontWeight: 'bold' }}>
                    Close
                </Button>
            </DialogActions>
        </Dialog>
        </>
    );
}
